import { Link } from 'react-router-dom';
import { MessageCircleIcon, MailIcon, PhoneIcon, MapPinIcon, ArrowRightIcon } from 'lucide-react';
import { PressStrip } from '../shared/PressStrip';
import { ProductCard } from '../shared/ProductCard';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { Reveal } from '../shared/Reveal';
import { CreatorRail } from './CreatorRail';
import { founder, hoursLabel, salon } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { policy, policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { isOpen } from '../../core/rules/schedule';
import { mediaOr } from '../../core/rules/media';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { fill } from '../../utils/copy';
import { homeBestsellers } from '../home/homeRules';
import { quietLink, sectionHeading } from '../../ui/sections';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';
import { PageHero } from '../shared/PageHero';
import { TeamSection } from './TeamSection';
import { staffList } from '../../core/rules/salonDays';

/** Tijdelijk sfeerbeeld (docs/BEELDEN.md, "tijdelijk, vervangen vóór livegang"): niet de salon zelf. */
const SALON_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/0ed04ad3-0600-47a3-bc34-389527c1fbf3.jpg';
/** Zoveel producten van "de plank van Sarah". */
const SHELF_COUNT = 3;

/** Alleen feiten die Sarah kan onderbouwen; geen opgeklopte getallen. */
const facts = (withColleague: boolean) => [{
  value: policyCopy.experience,
  label: 'in de huidzorg'
},
// Met een collega klopt "altijd door mij" niet meer.
{
  value: 'Eén',
  label: withColleague ? 'cliënt tegelijk, met alle aandacht' : 'cliënt tegelijk, altijd door mij behandeld'
}, {
  value: `${policy.responseHours} uur`,
  label: 'om persoonlijk te reageren op je bericht'
}];

/**
 * Over Sarah. Ritme (docs/VORMGEVING.md §3): hero met portret (sand) · verhaal en feiten (sand) ·
 * pers (shell) · filosofie (klei) · haar plank (shell) · wat cliënten gebruiken (sand) · de salon (blush).
 */
export function About() {
  const pages = usePages();
  const site = useSite();
  const {
    openingHours
  } = site;
  const withColleague = staffList(site.staff).some(s => s.role === 'staff' && s.active);
  const {
    openBooking
  } = useOverlayActions();
  const {
    products
  } = useCatalog();
  const copy = pages.about;
  const shelf = homeBestsellers(products, SHELF_COUNT);
  const portrait = mediaOr(copy.hero.media, {
    image: founder.portraitPalm,
    alt: `${founder.name}, ${founder.role}`,
    focus: {
      x: 50,
      y: 30
    }
  });
  const atWork = mediaOr(copy.philosophy.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een behandeling`
  });
  // De eerste alinea draagt de hero; de rest leest eronder rustig verder.
  const [lead, ...rest] = copy.hero.paragraphs;
  return <>
      {/* Hero: zelfde opbouw als salon en protocollen; het eerste scherm is het portret en haar belofte. */}
      <PageHero titleId="about-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} data-id="element-5523" data-mp="32t6pef22hs0m" />} body={lead ? fill(lead) : undefined} media={portrait} byline={{
      lead: <span className="text-ink" data-id="element-5524" data-mp="2ys4prx48ndy4">{founder.name}</span>,
      sub: `${founder.role} · ${salon.city}`
    }} actions={(copy.hero.cta || copy.hero.secondaryCta) && <>
              {copy.hero.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-5525" data-mp="2jv49cprhz5ey">
                  {fill(copy.hero.cta)}
                </button>}
              {copy.hero.secondaryCta && <Link to="/routines" className={btnSecondaryLg} data-id="element-5526" data-mp="i2g0o0w33prs">
                  {fill(copy.hero.secondaryCta)}
                </Link>}
            </>} data-id="element-5522" data-mp="37kopl0w9nokj" />

      {/* Het verhaal, en de feiten die ze kan onderbouwen. Op de telefoon niet meer in het eerste scherm. */}
      {(rest.length > 0 || facts(withColleague).length > 0) && <section className="bg-sand pb-20 sm:pb-24 lg:pb-28" aria-label={`Over ${founder.firstName}`} data-id="element-5527" data-mp="c576v65zun8p">
          <div className="mx-auto grid max-w-content gap-12 border-t border-ink/15 px-6 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pt-16" data-id="element-5528" data-mp="g5pz31bstvbf">
            {rest.length > 0 && <div className="max-w-2xl space-y-5 text-[17px] leading-relaxed text-muted lg:col-span-7" data-id="element-5529" data-mp="75kut01wjd1h">
                {rest.map((p, i) => <p key={`${i}-${p.slice(0, 24)}`} data-id="element-5530" data-mp="3ondry63c5qky" data-mpi={i}>{fill(p)}</p>)}
              </div>}
            {/* Getal + uitleg lezen als één zin: "25 jaar in de huidzorg". */}
            <ul className="grid gap-6 sm:grid-cols-3 lg:col-span-5 lg:grid-cols-1 lg:gap-0 lg:divide-y lg:divide-ink/15" data-id="element-5531" data-mp="3fmfn5ll2pf1b">
              {facts(withColleague).map(f => <li key={f.label} className="lg:flex lg:items-baseline lg:gap-5 lg:py-5 lg:first:pt-0" data-id="element-5532" data-mp="30y5spt0lhgwj">
                  <span className="block font-display text-[2rem] font-light text-ink lg:w-28 lg:shrink-0" data-id="element-5533" data-mp="2w04eble465re">{f.value}</span>
                  <span className="mt-1.5 block text-[14px] leading-snug text-muted lg:mt-0" data-id="element-5534" data-mp="3a05cgt2zuz36">{f.label}</span>
                </li>)}
            </ul>
          </div>
        </section>}

      <TeamSection data-id="element-5535" data-mp="3jw8n6nbww61q" />

      <PressStrip data-id="element-5536" data-mp="3jvp8dfhk5xrx" />

      {/* Filosofie: het ene klei-vlak van deze pagina */}
      <section className="bg-clay text-shell" aria-label="Filosofie" data-id="element-5537" data-mp="3exnk8g59z89l">
        <div className="grid lg:grid-cols-12" data-id="element-5538" data-mp="3tnmieiwtyjp2">
          <figure className="flex flex-col justify-center px-6 py-20 sm:px-10 lg:col-span-7 lg:py-28 lg:pl-10 lg:pr-16 xl:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]" data-id="element-5539" data-mp="3tmjw6oagd5fh">
            <p className="text-[14px] text-shell" data-id="element-5540" data-mp="6jf1s0wgvxv5">{fill(copy.philosophy.eyebrow)}</p>
            <blockquote className="mt-5 max-w-2xl font-display text-[2.6rem] font-light leading-[1.04] tracking-[-0.015em] sm:text-[3.4rem] lg:text-[4rem]" data-id="element-5541" data-mp="1kbwqgghlrn6">
              “{fill(copy.philosophy.claim)}.”
            </blockquote>
            <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-shell" data-id="element-5542" data-mp="30b4vieyntcz4">{fill(copy.philosophy.body)}</p>
            <figcaption className="mt-8 font-display text-2xl italic text-blush" data-id="element-5543" data-mp="34a3znxckh5pi">{founder.firstName}</figcaption>
          </figure>
          <Reveal className="lg:col-span-5" data-id="element-5544" data-mp="3e2a1rzwj93if">
            <MediaView media={atWork} sizes="(min-width: 1024px) 42vw, 100vw" className="aspect-[4/5] w-full sm:aspect-[16/11] lg:aspect-auto lg:h-full lg:min-h-[640px]" data-id="element-5545" data-mp="3e18gz3ppt2ni" />
          </Reveal>
        </div>
      </section>

      <section className="bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="about-shelf" data-id="element-5546" data-mp="3o0zcn8q0hnp6">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-5547" data-mp="3s0zyvfjcr36s">
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-5548" data-mp="1tmyekq2ad4q5">
            <div className="max-w-2xl" data-id="element-5549" data-mp="1xni87jnnhq7j">
              <h2 id="about-shelf" className={`${sectionHeading} text-ink`} data-id="element-5550" data-mp="1xojmdwzyqsth">
                <HeadingText heading={copy.shelf.heading} data-id="element-5551" data-mp="1q4iote06vyo6" />
              </h2>
              {copy.shelf.body && <p className="mt-6 text-[17px] leading-relaxed text-muted" data-id="element-5552" data-mp="1ldafehyv0db0">{fill(copy.shelf.body)}</p>}
            </div>
            {copy.shelf.cta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-5553" data-mp="1gcd1wioso7fu">
                {fill(copy.shelf.cta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5554" data-mp="1cb19k5ejd83y" />
              </Link>}
          </div>

          <ul className="mt-14 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-6 lg:grid-cols-3" data-id="element-5555" data-mp="24539ygtku6ku">
            {shelf.map(p => <li key={p.id} className="h-full" data-id="element-5556" data-mp="200vl8jn4cqmm">
                <ProductCard product={p} data-id="element-5557" data-mp="1v3vc1zuz37ra" />
              </li>)}
          </ul>
        </div>
      </section>

      <CreatorRail data-id="element-5558" data-mp="onjk4giqum45" />

      <section className="bg-blush py-20 sm:py-24 lg:py-32" aria-labelledby="about-salon" data-id="element-5559" data-mp="oour0p7hk9zh">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-5560" data-mp="jndroy4ngvl0">
          <div className="max-w-2xl" data-id="element-5561" data-mp="2jqofuz3we0pw">
            {copy.salon.eyebrow && <p className="text-[14px] text-ink" data-id="element-5562" data-mp="2jsiyu7vlzirc">{fill(copy.salon.eyebrow)}</p>}
            <h2 id="about-salon" className={`mt-4 ${sectionHeading} text-ink`} data-id="element-5563" data-mp="2ntl1yzsnceyl">
              <HeadingText heading={copy.salon.heading} data-id="element-5564" data-mp="29ta7lms4oi8o" />
            </h2>
            {copy.salon.body && <p className="mt-6 text-[17px] leading-relaxed text-muted" data-id="element-5565" data-mp="2eqjy75r96zy3">{fill(copy.salon.body)}</p>}
          </div>

          <div className="mt-14 grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-5566" data-mp="32i8qwn7zlqfk">
            <Reveal className="lg:col-span-7" data-id="element-5567" data-mp="37girjdchdj8k">
              <MediaView media={{
              image: SALON_IMAGE,
              alt: 'Sfeerbeeld: een rustige ruimte met daglicht en linnen'
            }} sizes="(min-width: 1024px) 55vw, 100vw" className="aspect-[3/2] w-full" data-id="element-5568" data-mp="37f7ojfqv0fii" />
            </Reveal>

            <div className="lg:col-span-5" data-id="element-5569" data-mp="2xnuseby1336o">
              <div className="border-t border-ink/15 pt-6" data-id="element-5570" data-mp="3lfigkm3wo059">
                <h3 className="font-display text-2xl font-light text-ink" data-id="element-5571" data-mp="3q6i9lg9wly2h">Bezoek &amp; contact</h3>
                <address className="mt-4 space-y-4 text-[15px] not-italic" data-id="element-5572" data-mp="1erjfao8iu9ev">
                  <p className="flex gap-3" data-id="element-5573" data-mp="1epopowmb1t4g">
                    <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-5574" data-mp="19syfur13ftrh" />
                    <span className="text-ink" data-id="element-5575" data-mp="1nis16zde3vwg">
                      {salon.street}
                      <br data-id="element-5576" data-mp="1jhzsc78fjt0p" />
                      {salon.postalCode} {salon.locality} · op afspraak
                      <br data-id="element-5577" data-mp="vrvpi232fxar" />
                      <a href={salon.routeUrl} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center text-[14px] text-muted underline underline-offset-4 hover:text-ink" data-id="element-5578" data-mp="vt74yz5vithp">
                        Plan je route<span className="sr-only" data-id="element-5579" data-mp="14un3o9nbt763"> {shopLabels.newTab}</span>
                      </a>
                    </span>
                  </p>
                  <p className="flex gap-3" data-id="element-5580" data-mp="2g7zb0uiao7x0">
                    <MailIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-5581" data-mp="2g6nwbznb9fr4" />
                    <a href={`mailto:${salon.email}`} className="break-all text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-5582" data-mp="2bbibl45na74m">
                      <span className="sr-only" data-id="element-5583" data-mp="2k6xzmn82huau">E-mail: </span>
                      {salon.email}
                    </a>
                  </p>
                  <p className="flex gap-3" data-id="element-5584" data-mp="2feogbq1gjshj">
                    <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-5585" data-mp="2bffkmfmuxkjc" />
                    <a href={`tel:${salon.phoneIntl}`} className="text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-5586" data-mp="26g2wzso5w9fp">
                      <span className="sr-only" data-id="element-5587" data-mp="21fezco5fguci">Bel: </span>
                      {salon.phone} (ook WhatsApp)
                    </a>
                  </p>
                </address>
                <p className="mt-5 text-[14px] leading-relaxed text-muted" data-id="element-5588" data-mp="1shd0ae598brv">
                  {fill(copy.salon.contactNote)} {salon.englishNote}.
                </p>
              </div>

              <div className="mt-8 border-t border-ink/15 pt-6" data-id="element-5589" data-mp="3mv48d97xqnl7">
                <h3 className="font-display text-2xl font-light text-ink" data-id="element-5590" data-mp="18mjtnatda59h">Openingstijden · op afspraak</h3>
                <dl className="mt-4 space-y-1.5 text-[15px]" data-id="element-5591" data-mp="1dl4rx4p26eva">
                  {openingHours.map(h => <div key={h.day} className="flex justify-between gap-4" data-id="element-5592" data-mp="1iblbl6jh2wq3">
                      <dt className="text-muted" data-id="element-5593" data-mp="1mdouuhcdv5lx">{h.day}</dt>
                      <dd className={isOpen(h) ? 'text-ink' : 'text-muted'} data-id="element-5594" data-mp="1mcx17c2cjhya">{hoursLabel(h)}</dd>
                    </div>)}
                </dl>
                <p className="mt-4 text-[13px] text-muted" data-id="element-5595" data-mp="ulqx6kji903q">KvK {salon.kvk}</p>
              </div>

              <div className="mt-9 flex flex-col gap-3" data-id="element-5596" data-mp="ynulwav6q16y">
                {copy.salon.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={`${btnPrimaryLg} w-full`} data-id="element-5597" data-mp="13nqhblptgkaw">
                    {fill(copy.salon.cta)}
                  </button>}
                {copy.salon.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${btnSecondaryLg} w-full`} data-id="element-5598" data-mp="2f46zhmsr9f1g">
                    <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5599" data-mp="aysx4pfger7l" />
                    {fill(copy.salon.secondaryCta)}
                    <span className="sr-only" data-id="element-5600" data-mp="oxsc15d5zrqk">{shopLabels.viaWhatsApp}</span>
                  </a>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}